import { NotificationReadI } from '../../../types'
import { TASK_PRIORITY_LIST } from '../../../utils/taskPriority'
import {
    NotificationGroup,
    NotificationMeta,
    NotificationRow,
    NotificationType,
} from './mock'

// Ключи соответствуют ActivityType бэкенда (created, updated, status_changed, commented,
// assigned, unassigned, attachment_added, time_logged) и реальным типам уведомлений
// с префиксом task_ (task_comment_added, task_mentioned и т.д.).
const ACTION_TYPE_MAP: Record<string, { type: NotificationType; action: string }> = {
    task_assigned: { type: 'assign', action: 'Назначил(а) вам задачу:' },
    assigned: { type: 'assign', action: 'Назначил(а) вам задачу:' },
    unassigned: { type: 'delegate', action: 'Снял(а) вас с задачи:' },
    task_unassigned: { type: 'delegate', action: 'Снял(а) вас с задачи:' },
    task_delegated: { type: 'delegate', action: 'Поручил(а) вам задачу:' },
    delegated: { type: 'delegate', action: 'Поручил(а) вам задачу:' },
    created: { type: 'assign', action: 'Создал(а) задачу:' },
    task_created: { type: 'assign', action: 'Создал(а) задачу:' },
    updated: { type: 'status', action: 'Обновил(а) задачу:' },
    task_updated: { type: 'status', action: 'Обновил(а) задачу:' },
    status_changed: { type: 'status', action: 'Изменил(а) статус в:' },
    task_status_changed: { type: 'status', action: 'Изменил(а) статус в:' },
    task_completed: { type: 'status', action: 'Завершил(а) задачу:' },
    priority_changed: { type: 'priority', action: 'Изменил(а) приоритет в:' },
    task_priority_changed: { type: 'priority', action: 'Изменил(а) приоритет в:' },
    commented: { type: 'comment', action: 'Оставил(а) комментарий в:' },
    comment_added: { type: 'comment', action: 'Оставил(а) комментарий в:' },
    task_comment_added: { type: 'comment', action: 'Оставил(а) комментарий в:' },
    task_commented: { type: 'comment', action: 'Оставил(а) комментарий в:' },
    mentioned: { type: 'mention', action: 'Упомянул(а) вас в:' },
    task_mentioned: { type: 'mention', action: 'Упомянул(а) вас в:' },
    comment_mentioned: { type: 'mention', action: 'Упомянул(а) вас в комментарии к:' },
    attachment_added: { type: 'attach', action: 'Прикрепил(а) файл в:' },
    task_attachment_added: { type: 'attach', action: 'Прикрепил(а) файл в:' },
    file_attached: { type: 'attach', action: 'Прикрепил(а) файл в:' },
    time_logged: { type: 'deadline', action: 'Записал(а) время в:' },
    task_time_logged: { type: 'deadline', action: 'Записал(а) время в:' },
    due_date_changed: { type: 'deadline', action: 'Изменил(а) срок в:' },
    task_due_date_changed: { type: 'deadline', action: 'Изменил(а) срок в:' },
    task_archived: { type: 'status', action: 'Архивировал(а) задачу:' },
    task_deadline: { type: 'deadline', action: 'Истекает срок выполнения задачи:' },
    deadline_approaching: { type: 'deadline', action: 'Истекает срок выполнения задачи:' },
    deadline: { type: 'deadline', action: 'Истекает срок выполнения задачи:' },
}

const WARNING_ACTION_TYPES = new Set(['deadline_approaching', 'deadline', 'task_deadline'])

const STATUS_COLOR_MAP: Record<string, string> = {
    'backlog': '#B1AEC7',
    'to do': '#F04438',
    'todo': '#F04438',
    'in progress': '#F79009',
    'in review': '#453CFB',
    'done': '#12B76A',
    'closed': '#12B76A',
}

// Названия и цвета приоритетов — те же, что в едит-моде задачи (TASK_PRIORITY_LIST).
const PRIORITY_MAP: Record<string, { label: string; color: string }> = TASK_PRIORITY_LIST.reduce(
    (map, priority) => {
        map[priority.id] = { label: priority.title, color: priority.color }
        map[priority.title.toLowerCase()] = { label: priority.title, color: priority.color }
        return map
    },
    { critical: { label: 'Срочный', color: '#E62F2F' } } as Record<string, { label: string; color: string }>,
)

function statusColor(status: string, explicit?: string): string {
    return explicit || STATUS_COLOR_MAP[status.trim().toLowerCase()] || '#B1AEC7'
}

function priorityInfo(priority: string): { label: string; color: string } {
    return PRIORITY_MAP[priority.trim().toLowerCase()] || { label: priority, color: '#B1AEC7' }
}

// details приходит как any: может быть объектом, JSON-строкой или обычной строкой.
function normalizeDetails(details: unknown): Record<string, any> | null {
    if (details == null) {
        return null
    }

    if (typeof details === 'object') {
        return details as Record<string, any>
    }

    if (typeof details === 'string') {
        try {
            const parsed = JSON.parse(details)
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, any>) : null
        } catch {
            return null
        }
    }

    return null
}

function pick(details: Record<string, any> | null | undefined, keys: string[]): any {
    if (!details || typeof details !== 'object') {
        return undefined
    }

    for (const key of keys) {
        if (details[key] != null) {
            return details[key]
        }
    }

    return undefined
}

// Файл в details: явные ключи, массив вложений (attachments/files), либо
// name/title — последние только когда сам тип уведомления «файл» (allowLooseKeys).
function extractFileMeta(details: Record<string, any> | null, allowLooseKeys: boolean): NotificationMeta | undefined {
    const fileArray = details?.attachments || details?.files
    const firstFile = Array.isArray(fileArray) && fileArray.length > 0 ? fileArray[0] : null

    const label = pick(details, ['file_name', 'filename', 'attachment_name'])
        ?? (firstFile ? pick(firstFile, ['file_name', 'filename', 'attachment_name', 'name', 'title']) : undefined)
        ?? (allowLooseKeys ? pick(details, ['name', 'title']) : undefined)

    if (label == null) {
        return undefined
    }

    const fileUrl = pick(details, ['file_url', 'fileUrl', 'url', 'download_url'])
        ?? (firstFile ? pick(firstFile, ['file_url', 'fileUrl', 'url', 'download_url']) : undefined)
    const attachmentId = pick(details, ['attachment_id', 'attachmentId'])
        ?? (firstFile ? pick(firstFile, ['id', 'attachment_id']) : undefined)

    return {
        kind: 'file',
        label: String(label),
        fileUrl: fileUrl != null ? String(fileUrl) : undefined,
        attachmentId: attachmentId != null ? String(attachmentId) : undefined,
    }
}

function buildMeta(row: NotificationReadI, type?: NotificationType): NotificationMeta | undefined {
    const details = normalizeDetails(row.details)

    const statusFromKey = pick(details, ['old_status', 'from_status', 'status_from', 'previous_status', 'old_status_key'])
    const statusToKey = pick(details, ['new_status', 'to_status', 'status_to', 'current_status', 'new_status_key'])
    if (statusFromKey != null && statusToKey != null) {
        // Читаемые названия, если бэкенд их прислал; иначе показываем ключ,
        // пока страница не подтянет статусы проекта (см. resolveStatusMeta).
        const statusFromName = pick(details, ['old_status_name', 'from_status_name', 'old_status_title'])
        const statusToName = pick(details, ['new_status_name', 'to_status_name', 'new_status_title'])

        return {
            kind: 'status',
            from: String(statusFromName ?? statusFromKey),
            to: String(statusToName ?? statusToKey),
            fromKey: String(statusFromKey),
            toKey: String(statusToKey),
            fromColor: statusColor(String(statusFromKey), pick(details, ['old_status_color', 'from_status_color'])),
            toColor: statusColor(String(statusToKey), pick(details, ['new_status_color', 'to_status_color'])),
        }
    }

    const priorityFrom = pick(details, ['old_priority', 'from_priority', 'priority_from', 'previous_priority'])
    const priorityTo = pick(details, ['new_priority', 'to_priority', 'priority_to', 'current_priority'])
    if (priorityFrom != null && priorityTo != null) {
        const from = priorityInfo(String(priorityFrom))
        const to = priorityInfo(String(priorityTo))

        return {
            kind: 'priority',
            from: from.label,
            to: to.label,
            fromColor: from.color,
            toColor: to.color,
        }
    }

    return extractFileMeta(details, type === 'attach')
}

function buildAuthor(row: NotificationReadI): string {
    const name = [row.source_user_first_name, row.source_user_last_name].filter(Boolean).join(' ').trim()

    return name || row.source_user_email || 'Система'
}

// id проекта: из details, а если его там нет — из ссылки на задачу (/projects/{id}...).
function buildProjectId(row: NotificationReadI, details: Record<string, any> | null): string | null {
    const projectId = pick(details, ['project_id', 'projectId'])
    if (projectId != null) {
        return String(projectId)
    }

    const linkMatch = row.task_link?.match(/\/projects\/([^/?#]+)/)

    return linkMatch ? linkMatch[1] : null
}

function buildTaskLink(row: NotificationReadI, projectId: string | null): string | null {
    // Ссылка бэка в приоритете: если она ведёт на другой хост (напр. дев-стенд),
    // переходим по ней полной загрузкой, а не пересобираем локальную.
    if (row.task_link) {
        // Ссылке может не хватать ?task= — без него откроется только проект, а не задача.
        try {
            const url = new URL(row.task_link, window.location.origin)

            if (row.task_id != null && !url.searchParams.get('task')) {
                url.searchParams.set('view', url.searchParams.get('view') || 'board')
                url.searchParams.set('task', String(row.task_id))
            }

            return url.origin === window.location.origin ? url.pathname + url.search : url.toString()
        } catch {
            return row.task_link
        }
    }

    // Фолбэк, когда бэкенд не прислал ссылку: собираем дип-линк сами.
    if (row.task_id != null && projectId != null) {
        return `/projects/${projectId}?view=board&task=${row.task_id}`
    }

    return null
}

function startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

function resolveGroup(created: Date, now: Date): NotificationGroup {
    if (Number.isNaN(created.getTime())) {
        return 'earlierThisMonth'
    }

    const dayMs = 24 * 60 * 60 * 1000
    const diffDays = Math.floor((startOfDay(now) - startOfDay(created)) / dayMs)

    if (diffDays <= 0) {
        return 'today'
    }

    if (diffDays === 1) {
        return 'yesterday'
    }

    if (diffDays < 7) {
        return 'earlierThisWeek'
    }

    return 'earlierThisMonth'
}

// Русская плюрализация: plural(5, ['минуту', 'минуты', 'минут']) -> 'минут'
function plural(count: number, forms: [string, string, string]): string {
    const mod10 = count % 10
    const mod100 = count % 100

    if (mod10 === 1 && mod100 !== 11) {
        return forms[0]
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return forms[1]
    }

    return forms[2]
}

// Живое относительное время: страница пересчитывает его по таймеру, передавая свежий nowMs.
export function formatNotificationTime(createdAtMs: number, nowMs: number): string {
    if (!createdAtMs) {
        return ''
    }

    const created = new Date(createdAtMs)
    const diffMs = nowMs - createdAtMs
    const diffMinutes = Math.floor(diffMs / (60 * 1000))

    if (diffMinutes < 1) {
        return 'Сейчас'
    }

    if (diffMinutes < 60) {
        return `${diffMinutes} ${plural(diffMinutes, ['минуту', 'минуты', 'минут'])} назад`
    }

    const diffHours = Math.floor(diffMinutes / 60)
    if (diffHours < 24) {
        return `${diffHours} ${plural(diffHours, ['час', 'часа', 'часов'])} назад`
    }

    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) {
        return `${diffDays} ${plural(diffDays, ['день', 'дня', 'дней'])} назад`
    }

    const dd = String(created.getDate()).padStart(2, '0')
    const mm = String(created.getMonth() + 1).padStart(2, '0')
    const yyyy = created.getFullYear()
    const hh = String(created.getHours()).padStart(2, '0')
    const min = String(created.getMinutes()).padStart(2, '0')

    return `${dd}/${mm}/${yyyy} в ${hh}:${min}`
}

export function mapNotification(row: NotificationReadI, now: Date = new Date()): NotificationRow {
    const mapped = ACTION_TYPE_MAP[row.action_type]
    const warning = WARNING_ACTION_TYPES.has(row.action_type)

    if (!mapped) {
        console.warn(`[notifications] Неизвестный action_type "${row.action_type}" — нет маппинга в ACTION_TYPE_MAP`)
    }
    const created = new Date(row.created_at)
    const projectId = buildProjectId(row, normalizeDetails(row.details))
    const meta = buildMeta(row, mapped?.type)

    let type = mapped?.type ?? 'assign'
    let action = mapped?.action ?? row.action_type

    // Комментарий с вложением бэкенд шлёт как task_comment_added —
    // если в details нашёлся файл, показываем уведомление как «Прикрепил(а) файл».
    // Упоминания не переопределяем: @-уведомление важнее файла.
    if (meta?.kind === 'file' && type === 'comment') {
        type = 'attach'
        action = 'Прикрепил(а) файл в:'
    }

    return {
        id: String(row.id),
        tab: 'task',
        type,
        author: warning ? 'Предупреждение (?)' : buildAuthor(row),
        avatarUrl: warning ? null : row.source_user_photo_url,
        action,
        target: row.task_title ?? '',
        meta,
        createdAtMs: Number.isNaN(created.getTime()) ? 0 : created.getTime(),
        group: resolveGroup(created, now),
        taskLink: buildTaskLink(row, projectId),
        taskId: row.task_id ?? null,
        projectId,
        unread: !row.is_read,
        warning,
    }
}

export function mapNotifications(rows: NotificationReadI[], now: Date = new Date()): NotificationRow[] {
    return rows.map((row) => mapNotification(row, now))
}
